// Where the staff password link lands. The token in the query string is
// the whole credential - the visitor arrives with no session - and setting
// the password signs them in, unless an authenticator app is connected:
// then the code is still owed before a session is handed out.

import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  MIN_PASSWORD_LENGTH,
  postMfaCode,
  setStaffPassword,
  STORAGE_REFUSED_MESSAGE,
  storeToken,
  type StaffMfaChallenge,
} from "../api/auth";
import { AuthBrand } from "../components/auth/AuthBrand";

export default function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An account with an authenticator app connected saves the password but
  // still owes the code before a session is handed out.
  const [mfa, setMfa] = useState<StaffMfaChallenge | null>(null);
  const [code, setCode] = useState("");

  function completeSignIn(sessionToken: string) {
    if (!storeToken(sessionToken)) {
      setError(STORAGE_REFUSED_MESSAGE);
      return;
    }
    navigate("/", { replace: true });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== repeat) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await setStaffPassword(token, password);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      if ("mfa" in result) {
        setMfa(result.mfa);
        setCode("");
        return;
      }
      completeSignIn(result.token);
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onCode(e: FormEvent) {
    e.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setError(null);
    try {
      const result = await postMfaCode(mfa.pendingToken, code);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      // The code step never answers with a fresh challenge.
      if ("mfa" in result) return;
      completeSignIn(result.token);
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <div className="auth-head">
            <AuthBrand />
            <div className="auth-sub">Choose your password</div>
          </div>
          <p className="auth-ok">
            This link is missing the part that identifies you. Open it again from the email, or ask
            for a new one from the sign-in page.
          </p>
          <Link className="auth-link-btn" to="/login">
            Back to sign-in
          </Link>
        </div>
      </main>
    );
  }

  if (mfa) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <div className="auth-head">
            <AuthBrand />
            <div className="auth-sub">Finish signing in</div>
          </div>

          {error ? (
            <div role="alert" className="auth-alert">
              {error}
            </div>
          ) : null}

          <p role="status" className="auth-ok">
            Your password is saved. Enter the 6-digit code from your authenticator app, or one of
            your recovery codes, to sign in.
          </p>
          <form className="auth-form" onSubmit={onCode}>
            <label className="auth-field">
              <span>Code</span>
              <input
                type="text"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? "Checking..." : "Sign in"}
            </button>
          </form>
          <Link className="auth-link-btn" to="/login">
            Back to sign-in
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-head">
          <AuthBrand />
          <div className="auth-sub">Choose your password</div>
        </div>

        {error ? (
          <div role="alert" className="auth-alert">
            {error}
          </div>
        ) : null}

        <form className="auth-form" onSubmit={onSubmit}>
          <label className="auth-field">
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <small>At least {MIN_PASSWORD_LENGTH} characters.</small>
          </label>
          <label className="auth-field">
            <span>Repeat password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? "Saving..." : "Set password and sign in"}
          </button>
        </form>

        <div className="auth-note">
          The link works once. If it has expired, ask for a new one from the sign-in page.
        </div>
        <Link className="auth-link-btn" to="/login">
          Back to sign-in
        </Link>
      </div>
    </main>
  );
}
